import { motion } from "motion/react";
import { memo, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib/utils";
import { useChart, useChartStable } from "./chart-context";

function BarXAxisLabel({
  label,
  x,
  crosshairX,
  isHovering,
  tickerHalfWidth
}) {
  const fadeBuffer = 20;
  const fadeRadius = tickerHalfWidth + fadeBuffer;

  let opacity = 1;
  if (isHovering && crosshairX !== null) {
    const distance = Math.abs(x - crosshairX);
    if (distance < tickerHalfWidth) {
      opacity = 0;
    } else if (distance < fadeRadius) {
      opacity = (distance - tickerHalfWidth) / fadeBuffer;
    }
  }

  // Zero-width container approach for perfect centering
  return (
    <div
      className="absolute"
      style={{
        left: x,
        bottom: 12,
        width: 0,
        display: "flex",
        justifyContent: "center",
      }}>
      <motion.span
        animate={{ opacity }}
        className={cn("whitespace-nowrap text-chart-label text-xs")}
        initial={{ opacity: 1 }}
        transition={{ duration: 0.4, ease: "easeInOut" }}>
        {label}
      </motion.span>
    </div>
  );
}

export function BarXAxis(props) {
  const { containerRef, barScale } = useChartStable();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const container = containerRef.current;
  if (!(mounted && container)) {
    return null;
  }

  if (!barScale) {
    return null;
  }

  return <BarXAxisInner {...props} container={container} />;
}

const BarXAxisInner = memo(function BarXAxisInner({
  tickerHalfWidth = 50,
  showAllLabels = false,
  maxLabels = 12,
  container
}) {
  const { margin, tooltipData, barScale, bandWidth, barXAccessor, data, innerWidth, dateLabels } =
    useChart();

  // Generate labels for each bar
  const labelsToShow = useMemo(() => {
    if (!(barScale && bandWidth && barXAccessor)) {
      return [];
    }

    const allLabels = data.map((d, i) => {
      const category = barXAccessor(d);
      const bandX = barScale(category) ?? 0;
      // Center the label under the bar group
      const x = bandX + bandWidth / 2 + margin.left;
      // Lodgeic extension: `xLabels` on the shell supplies display text per
      // index; the category string stays the scale key.
      const label = dateLabels?.[i] ?? category;
      return { label, x };
    });

    if (showAllLabels || allLabels.length === 0) {
      return allLabels;
    }

    // Thin by WIDTH, not just count: a handful of long labels ("Jan 2026")
    // in a narrow chart collide long before they hit `maxLabels`. Estimate
    // the widest label against its slot and skip every Nth until they fit.
    // ~6.6px/char approximates the 12px (text-xs) UI font.
    const slot = innerWidth > 0 ? innerWidth / allLabels.length : 0;
    const widestChars = allLabels.reduce(
      (max, item) => Math.max(max, String(item.label ?? '').length),
      0
    );
    const needed = widestChars * 6.6 + 14;
    const widthStep = slot > 0 ? Math.ceil(needed / slot) : 1;
    const countStep =
      allLabels.length > maxLabels ? Math.ceil(allLabels.length / maxLabels) : 1;
    const step = Math.max(1, widthStep, countStep);

    if (step <= 1) {
      return allLabels;
    }
    return allLabels.filter((_, i) => i % step === 0);
  }, [
    barScale,
    bandWidth,
    barXAccessor,
    dateLabels,
    data,
    margin.left,
    innerWidth,
    showAllLabels,
    maxLabels,
  ]);

  const isHovering = tooltipData !== null;
  const crosshairX = tooltipData ? tooltipData.x + margin.left : null;

  return createPortal(<div className="pointer-events-none absolute inset-0">
    {labelsToShow.map((item) => (
      <BarXAxisLabel
        crosshairX={crosshairX}
        isHovering={isHovering}
        key={`${item.label}-${item.x}`}
        label={item.label}
        tickerHalfWidth={tickerHalfWidth}
        x={item.x} />
    ))}
  </div>, container);
});

BarXAxis.displayName = "BarXAxis";

export default BarXAxis;
